import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { AuthMemberModel, AuthMemberSystemAccessModel } from "@/types/authType";

/**
 * CDMS system identifier — used to locate the current user's authorization
 * inside `member.systemaccess[]`. This is the only source of truth for
 * role-based access checks throughout the app.
 */
export const CDMS_SYSTEMNO = "1e31ae56-0211-449e-bb13-cddde6f4232b";

export type SystemAccess = AuthMemberSystemAccessModel;

export type AuthUser = AuthMemberModel & { accessToken?: string };

export type AuthSession = {
  member: AuthMemberModel;
  accessToken: string;
  expiration: string;
  systemAccess: SystemAccess;
};

type AuthCtx = {
  user: AuthUser | null;
  session: AuthSession | null;
  systemAccess: SystemAccess | null;
  initialized: boolean;
  login: (session: AuthSession) => void;
  logout: () => void;
  /** Role check against the CDMS system access record. */
  hasRole: (...rolenos: number[]) => boolean;
  /** Convenience: rolenos 1 (SUPER) or 2 (ADMIN). */
  isAdminLike: () => boolean;
  /** Partial update of the in-memory user (e.g. avatar / fullname after edit). */
  updateUser: (patch: Partial<AuthUser>) => void;
  /** Kept for API compatibility with existing callers — no-op after refactor. */
  refreshUser: () => Promise<void>;
};

/** Canonical label for a CDMS role number (1/2/3). */
export function roleLabel(roleno: number | null | undefined): string {
  switch (Number(roleno ?? 0)) {
    case 1:
      return "SUPER ADMINISTRATOR";
    case 2:
      return "ADMINISTRATOR";
    case 3:
      return "PERSONNEL";
    default:
      return "";
  }
}

/** Find the CDMS system-access record on a member payload. */
export function findCdmsAccess(member: AuthMemberModel | null | undefined): SystemAccess | null {
  const list = member?.systemaccess;
  if (!Array.isArray(list)) return null;
  const match = list.find(
    (s) => String(s?.systemno ?? "").toLowerCase() === CDMS_SYSTEMNO.toLowerCase(),
  );
  return match ?? null;
}

const Ctx = createContext<AuthCtx | null>(null);
const STORAGE_KEY = "cdms.session";
const LEGACY_KEY = "attendance.activeUser";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [initialized, setInitialized] = useState(false);

  const persist = (next: AuthSession | null) => {
    setSession(next);
    if (typeof window === "undefined") return;
    try {
      if (next) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        if (next.accessToken) window.localStorage.setItem("authToken", next.accessToken);
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem("authToken");
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") {
      setInitialized(true);
      return;
    }
    try {
      // Migration: discard pre-CDMS session shape.
      window.localStorage.removeItem(LEGACY_KEY);
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as AuthSession | null;
        if (parsed?.member && parsed?.accessToken && parsed?.systemAccess) {
          setSession(parsed);
        } else {
          window.localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
    setInitialized(true);
  }, []);

  const login = (next: AuthSession) => persist(next);

  const logout = () => {
    persist(null);
    if (typeof window !== "undefined") {
      try {
        window.location.replace("/");
      } catch {
        window.location.href = "/";
      }
    }
  };

  const hasRole = (...rolenos: number[]) => {
    const rn = session?.systemAccess?.roleno;
    return typeof rn === "number" && rolenos.includes(rn);
  };
  const isAdminLike = () => {
    const rn = session?.systemAccess?.roleno;
    return rn === 1 || rn === 2;
  };

  const updateUser = (patch: Partial<AuthUser>) => {
    setSession((prev) => {
      if (!prev) return prev;
      const nextMember = { ...prev.member, ...patch } as AuthMemberModel;
      const next: AuthSession = { ...prev, member: nextMember };
      try {
        if (typeof window !== "undefined")
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const refreshUser = async () => {
    /* no-op: login response is the single source of truth */
  };

  const user: AuthUser | null = session
    ? { ...session.member, accessToken: session.accessToken }
    : null;

  return (
    <Ctx.Provider
      value={{
        user,
        session,
        systemAccess: session?.systemAccess ?? null,
        initialized,
        login,
        logout,
        hasRole,
        isAdminLike,
        updateUser,
        refreshUser,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) {
    return {
      user: null,
      session: null,
      systemAccess: null,
      initialized: true,
      login: () => {},
      logout: () => {},
      hasRole: () => false,
      isAdminLike: () => false,
      updateUser: () => {},
      refreshUser: async () => {},
    } as AuthCtx;
  }
  return c;
}
